import React from 'react';
import {
    Route,
    Navigate,
    createBrowserRouter,
    createRoutesFromElements,
    RouterProvider,
} from 'react-router-dom';
import { routes, publicRoutes } from '@/routes/routes';
import { RouteType } from '@/models/RouteType';

import ErrorsLayout from '@/layouts/ErrorsLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';

import Page404 from '@/pages/Page404/Page404';
import Page500 from '@/pages/Page500/Page500';

import { RootState, useAppSelector } from '@/reduxStore/rootReducer';
import { checkIfUserHasAtLeastOneAllowedPermission } from '@/helpers/checkIfUserHasAtLeastOneAllowedPermission';
import { checkIfUserHasAllNecessaryPermissions } from '@/helpers/checkIfUserHasAllNecessaryPermissions';
import { useAuth0 } from '@auth0/auth0-react';
import { clientViewService } from '@/helpers/clientViewService';
import { UserPermission } from '@/api/client';
import ErrorsLayoutGrey from '@/layouts/ErrorsLayoutGrey';

const NO_PERMISSION_REDIRECTION_LINK = '/documentation';

const RoutePermissionChecker = ({
    children,
    necessaryPermissions,
    allowedPermissions,
    VACRequiredPermissions,
}: {
    children: any;
    necessaryPermissions?: string[];
    allowedPermissions?: string[];
    VACRequiredPermissions?: UserPermission[];
}) => {
    const userPermissions = useAppSelector((state: RootState) => state.roles.userPermissions);
    const isViewAsClient = clientViewService.isViewAsClient();

    if (isViewAsClient) {
        if (VACRequiredPermissions) {
            if (VACRequiredPermissions.length === 0) {
                return <>{children}</>;
            }
            const isHasNecessaryPermissions = checkIfUserHasAllNecessaryPermissions(
                VACRequiredPermissions,
                userPermissions,
            );
            if (!isHasNecessaryPermissions) {
                return <Navigate to={NO_PERMISSION_REDIRECTION_LINK} replace />;
            } else {
                return <>{children}</>;
            }
        }
        return <Navigate to={NO_PERMISSION_REDIRECTION_LINK} replace />;
    } else {
        const isHasNecessaryPermissions = checkIfUserHasAllNecessaryPermissions(
            necessaryPermissions,
            userPermissions,
        );
        const isHasAllowedPermission = checkIfUserHasAtLeastOneAllowedPermission(
            allowedPermissions,
            userPermissions,
        );
        if (!isHasNecessaryPermissions) {
            return <Navigate to={NO_PERMISSION_REDIRECTION_LINK} replace />;
        } else if (!isHasAllowedPermission) {
            return <Navigate to={NO_PERMISSION_REDIRECTION_LINK} replace />;
        } else {
            return <>{children}</>;
        }
    }
};

const childRoutes = (Layout: React.ElementType, routes: Array<RouteType>) => {
    return routes.map(
        (
            {
                component: Component,
                guard,
                children,
                path,
                nestedChildren,
                necessaryPermissions,
                allowedPermissions,
                VACRequiredPermissions,
            },
            index: number,
        ) => {
            if (children) {
                return children.map((element, index: number) => {
                    const ChildGuard = element.guard || React.Fragment;

                    return (
                        <Route
                            key={index}
                            path={element.path}
                            element={
                                <RoutePermissionChecker
                                    necessaryPermissions={necessaryPermissions}
                                    allowedPermissions={allowedPermissions}
                                    VACRequiredPermissions={VACRequiredPermissions}
                                >
                                    <ChildGuard>
                                        <Layout>
                                            <element.component />
                                        </Layout>
                                    </ChildGuard>
                                </RoutePermissionChecker>
                            }
                        />
                    );
                });
            }
            if (nestedChildren && Component) {
                return (
                    <Route
                        key={index}
                        path={path}
                        element={
                            <RoutePermissionChecker
                                necessaryPermissions={necessaryPermissions}
                                allowedPermissions={allowedPermissions}
                                VACRequiredPermissions={VACRequiredPermissions}
                            >
                                <Layout>
                                    <Component />
                                </Layout>
                            </RoutePermissionChecker>
                        }
                    >
                        {nestedChildren.map((nestedChild, index) => {
                            return (
                                <Route
                                    key={index}
                                    path={nestedChild.path}
                                    element={
                                        <RoutePermissionChecker
                                            necessaryPermissions={necessaryPermissions}
                                            allowedPermissions={allowedPermissions}
                                            VACRequiredPermissions={VACRequiredPermissions}
                                        >
                                            <nestedChild.component />
                                        </RoutePermissionChecker>
                                    }
                                />
                            );
                        })}
                    </Route>
                );
            }
            if (Component) {
                return (
                    <Route
                        key={index}
                        path={path}
                        element={
                            <RoutePermissionChecker
                                necessaryPermissions={necessaryPermissions}
                                allowedPermissions={allowedPermissions}
                                VACRequiredPermissions={VACRequiredPermissions}
                            >
                                <Layout>
                                    <Component />
                                </Layout>
                            </RoutePermissionChecker>
                        }
                    />
                );
            }
            return null;
        },
    );
};

const AppRoutes = () => {
    const { isAuthenticated } = useAuth0();

    const route = React.useMemo(
        () =>
            createBrowserRouter(
                createRoutesFromElements(
                    <>
                        {isAuthenticated && (
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        )}
                        {!isAuthenticated && (
                            <Route path="/dashboard" element={<Navigate to="/" replace />} />
                        )}
                        <Route
                            path="*"
                            element={
                                <ErrorsLayout>
                                    <Page404 />
                                </ErrorsLayout>
                            }
                        />
                        <Route
                            path="/error-500"
                            element={
                                <ErrorsLayout>
                                    <Page500 />
                                </ErrorsLayout>
                            }
                        />
                        {isAuthenticated && childRoutes(DashboardLayout, routes)}
                        {childRoutes(DashboardLayout, publicRoutes)}
                    </>,
                ),
            ),
        [isAuthenticated],
    );

    return <RouterProvider router={route} />;
};

export default AppRoutes;
